/** CS Fundamentals & Backend interview prep content. */

export const csFundamentalsContent = {
  "threads": {
    "title": "Threads",
    "subtitle": "Lightweight execution units inside a process — how software uses multiple CPU cores and stays responsive.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "A thread is the smallest unit of execution the OS schedules within a process. Threads in the same process share heap and globals but have separate stacks and program counters."
      },
      {
        "type": "h",
        "text": "Why threads matter"
      },
      {
        "type": "ul",
        "items": [
          "Parallel CPU-bound work across cores.",
          "Concurrent I/O-bound work — one thread waits while others run.",
          "Cheaper than processes for sharing in-memory state."
        ]
      },
      {
        "type": "diagram",
        "kind": "process-thread",
        "caption": "One process, shared heap, multiple thread stacks."
      },
      {
        "type": "table",
        "headers": [
          "",
          "Threads",
          "Processes"
        ],
        "rows": [
          [
            "Memory",
            "Shared address space",
            "Isolated"
          ],
          [
            "Creation",
            "Fast",
            "Slower (new address space)"
          ],
          [
            "Failure",
            "Can crash whole process",
            "Better isolation"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "title": "Strong answer",
        "text": "Contrast thread-per-request vs thread pool. Mention race conditions on shared mutable state and need for locks or message passing."
      },
      {
        "type": "code",
        "language": "java",
        "title": "Thread pool pattern",
        "code": "ExecutorService ex = Executors.newFixedThreadPool(16);\nex.submit(() -> processRequest(req));"
      }
    ],
    "references": [
      {
        "label": "OSTEP — Operating Systems",
        "url": "https://pages.cs.wisc.edu/~remzi/OSTEP/"
      }
    ]
  },
  "processes": {
    "title": "Processes",
    "subtitle": "An OS-managed running program with its own virtual memory and resources.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "A process is a program in execution: code, data, heap, stack, file descriptors, and security identity. The OS schedules processes and threads onto CPU cores."
      },
      {
        "type": "steps",
        "items": [
          {
            "title": "Create",
            "text": "fork() + exec() on Unix; parent may wait() for exit status."
          },
          {
            "title": "Run",
            "text": "Scheduler assigns CPU time slices (preemptive multitasking)."
          },
          {
            "title": "Block",
            "text": "Sleep on I/O, locks, or signals."
          },
          {
            "title": "Exit",
            "text": "Return exit code; OS reclaims resources."
          }
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Chrome uses multi-process architecture: renderer sandbox per tab limits blast radius of crashes and security bugs."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Know copy-on-write after fork — child initially shares pages with parent until either writes."
      },
      {
        "type": "h",
        "text": "Process vs thread — interview recap"
      },
      {
        "type": "table",
        "headers": [
          "Question",
          "Answer"
        ],
        "rows": [
          [
            "Who allocates memory?",
            "Process gets virtual address space; threads share it"
          ],
          [
            "IPC between processes?",
            "Pipes, sockets, shared memory (explicit)"
          ],
          [
            "Context switch cost?",
            "Higher across processes — MMU page table switch"
          ]
        ]
      },
      {
        "type": "code",
        "language": "bash",
        "title": "Inspect processes",
        "code": "ps aux | grep node\npstree -p <pid>"
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Explain why nginx uses worker processes + threads: isolation, utilize multiple cores, graceful reload by spawning new workers."
      }
    ],
    "references": [
      {
        "label": "OSTEP — Operating Systems",
        "url": "https://pages.cs.wisc.edu/~remzi/OSTEP/"
      }
    ]
  },
  "mutex": {
    "title": "Mutex",
    "subtitle": "Mutual exclusion lock — one thread in the critical section at a time.",
    "readingMinutes": 8,
    "blocks": [
      {
        "type": "p",
        "text": "Mutexes serialize access to shared mutable data (counters, caches, queues). Lock before read/write; unlock in all paths including errors."
      },
      {
        "type": "ul",
        "items": [
          "Keep critical sections tiny — never call slow I/O while holding a lock.",
          "Use consistent lock ordering to avoid deadlock.",
          "In Java: synchronized, ReentrantLock; in Go: sync.Mutex."
        ]
      },
      {
        "type": "code",
        "language": "java",
        "code": "synchronized (lock) {\n  balance += amount;\n}"
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Fine-grained locking is faster but harder to reason about — document lock order."
      },
      {
        "type": "h",
        "text": "Mutex vs spinlock"
      },
      {
        "type": "p",
        "text": "Spinlocks busy-wait for short critical sections (kernel). Mutexes sleep waiting threads — better when contention duration is unknown."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Reentrant mutex allows same thread to lock twice (Java ReentrantLock). Standard mutex deadlock if same thread locks twice."
      },
      {
        "type": "ul",
        "items": [
          "Avoid nested locks when possible.",
          "Use tryLock(timeout) for deadlock recovery paths.",
          "Profile before adding locks — lock-free structures may suffice for counters."
        ]
      }
    ],
    "references": [
      {
        "label": "OSTEP — Operating Systems",
        "url": "https://pages.cs.wisc.edu/~remzi/OSTEP/"
      }
    ]
  },
  "semaphore": {
    "title": "Semaphore",
    "subtitle": "Counting permits that limit how many threads use a resource simultaneously.",
    "readingMinutes": 8,
    "blocks": [
      {
        "type": "p",
        "text": "Semaphores track available slots. acquire() takes a permit (blocks if none); release() returns one. A binary semaphore (1 permit) acts like a mutex."
      },
      {
        "type": "table",
        "headers": [
          "Use case",
          "Semaphore type"
        ],
        "rows": [
          [
            "DB connection pool",
            "Counting — max N concurrent"
          ],
          [
            "Producer-consumer buffer",
            "Empty + full semaphores"
          ],
          [
            "Rate limiting workers",
            "Counting permits = max parallelism"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Mutex: only the thread that locked must unlock. Semaphore: any thread may release — important interview distinction."
      },
      {
        "type": "h",
        "text": "Classic producer-consumer setup"
      },
      {
        "type": "ol",
        "items": [
          "Producer acquires empty slot semaphore.",
          "Producer locks buffer mutex, writes item, unlocks.",
          "Producer releases full slot semaphore.",
          "Consumer reverses the pattern."
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Java Semaphore and C++ counting_semaphore map directly to OS concepts — good for whiteboard coding questions."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Bounded buffer problem is the canonical semaphore interview question — practice coding it."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "OSTEP — Operating Systems",
        "url": "https://pages.cs.wisc.edu/~remzi/OSTEP/"
      }
    ]
  },
  "deadlock": {
    "title": "Deadlock",
    "subtitle": "Circular waiting that freezes threads forever unless the OS or DB breaks the cycle.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "Deadlock requires: mutual exclusion, hold-and-wait, no preemption, circular wait. Break any condition to prevent it."
      },
      {
        "type": "diagram",
        "kind": "deadlock-cycle",
        "caption": "Thread 1: lock A → wait B. Thread 2: lock B → wait A."
      },
      {
        "type": "ul",
        "items": [
          "Global lock ordering on multiple resources.",
          "tryLock with timeout + retry jitter.",
          "Reduce lock scope; prefer immutability.",
          "Databases detect deadlocks and abort one transaction."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "title": "Real example",
        "text": "Two transactions update rows 1 and 2 in opposite order → InnoDB picks a victim and rolls back one transaction."
      },
      {
        "type": "h",
        "text": "Detection vs prevention"
      },
      {
        "type": "p",
        "text": "Databases detect deadlocks periodically and abort one transaction. Applications should prevent via lock ordering rather than rely on detection."
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Visualize wait-for graph: nodes are threads/resources; cycle means deadlock."
      },
      {
        "type": "code",
        "language": "sql",
        "title": "Consistent lock order",
        "code": "-- Always lock rows in ascending id order\nSELECT * FROM accounts WHERE id IN (1, 5) ORDER BY id FOR UPDATE;"
      }
    ],
    "references": [
      {
        "label": "OSTEP — Operating Systems",
        "url": "https://pages.cs.wisc.edu/~remzi/OSTEP/"
      }
    ]
  },
  "paging-and-virtual-memory": {
    "title": "Paging & Virtual Memory",
    "subtitle": "Each process sees a private address space mapped to physical RAM and disk by the MMU.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "Virtual memory decouples logical addresses from physical RAM. Pages (typically 4 KB) map via page tables; faults load from swap when absent."
      },
      {
        "type": "ul",
        "items": [
          "Isolation — processes cannot read each other's memory.",
          "Overcommit — total virtual size can exceed RAM until pressure hits.",
          "Copy-on-write — efficient fork and lazy allocation."
        ]
      },
      {
        "type": "formula",
        "latex": "EAT = (1 - p) × memory_access + p × page_fault_cost",
        "meaning": "Page fault cost is huge (microseconds to milliseconds) — avoid thrashing."
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Linux OOM killer terminates processes when memory is exhausted — know your container limits."
      },
      {
        "type": "h",
        "text": "Interview follow-ups"
      },
      {
        "type": "ol",
        "items": [
          "What is a page fault? Major vs minor?",
          "What is the TLB and why does it matter?",
          "How does malloc relate to virtual memory?",
          "What happens when you run out of RAM?"
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Connect to backend: large heap + small container limit → OOMKill even if JVM thinks memory is available."
      },
      {
        "type": "diagram",
        "kind": "memory-hierarchy",
        "caption": "Registers → cache → RAM → disk swap — each level slower but larger."
      }
    ],
    "references": [
      {
        "label": "OSTEP — Operating Systems",
        "url": "https://pages.cs.wisc.edu/~remzi/OSTEP/"
      }
    ]
  },
  "cpu-scheduling": {
    "title": "CPU Scheduling",
    "subtitle": "The policy that decides which ready thread runs on each CPU core.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "Schedulers trade fairness, throughput, and latency. Linux uses Completely Fair Scheduler (CFS) by default — tracks virtual runtime in a red-black tree."
      },
      {
        "type": "table",
        "headers": [
          "Algorithm",
          "Behavior"
        ],
        "rows": [
          [
            "FCFS",
            "Simple queue; long tasks block short ones"
          ],
          [
            "Round Robin",
            "Time quantum; good for interactive"
          ],
          [
            "Priority",
            "Risk of starvation"
          ],
          [
            "CFS",
            "Proportional fair CPU share"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Preemptive scheduling interrupts long-running threads — cooperative models (early coroutines) need explicit yield."
      },
      {
        "type": "h",
        "text": "Multilevel feedback queue (concept)"
      },
      {
        "type": "p",
        "text": "Interactive jobs start in high priority; CPU-bound jobs demote over time. Linux niceness (-20 to 19) adjusts CFS weight — useful for batch jobs on shared servers."
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Container CPU throttling (cfs_quota) can make apps look CPU-bound when limit is too low — check cgroup metrics."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Real-time scheduling (SCHED_FIFO) exists but most backend servers use CFS — mention only if asked."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "OSTEP — Operating Systems",
        "url": "https://pages.cs.wisc.edu/~remzi/OSTEP/"
      }
    ]
  },
  "context-switching": {
    "title": "Context Switching",
    "subtitle": "Saving and restoring CPU state when switching between threads or processes.",
    "readingMinutes": 8,
    "blocks": [
      {
        "type": "p",
        "text": "Context switches save registers, stack pointer, program counter, and may switch address spaces. They invalidate CPU cache hotness — a hidden cost of too many threads."
      },
      {
        "type": "ul",
        "items": [
          "Triggered by timer ticks, I/O wakeups, lock contention.",
          "Thread pools amortize creation cost and cap scheduler load.",
          "Async I/O reduces blocking threads — fewer kernel switches for I/O-bound servers."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "title": "Follow-up",
        "text": "Why not 1 thread per HTTP request at 10k RPS? Memory for stacks + scheduler overhead + context switch storms."
      },
      {
        "type": "h",
        "text": "Measuring impact"
      },
      {
        "type": "p",
        "text": "vmstat and pidstat show cs (context switch) rates. Spikes under load may indicate too many runnable threads or lock contention."
      },
      {
        "type": "table",
        "headers": [
          "Pattern",
          "Likely cause"
        ],
        "rows": [
          [
            "High cs + high CPU",
            "Too many threads competing"
          ],
          [
            "High cs + low CPU",
            "I/O wait, threads waking frequently"
          ],
          [
            "Low cs + high CPU",
            "Healthy CPU-bound work"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Go goroutines and Java virtual threads reduce OS thread count for I/O workloads — fewer heavyweight context switches."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "OSTEP — Operating Systems",
        "url": "https://pages.cs.wisc.edu/~remzi/OSTEP/"
      }
    ]
  },
  "normalization": {
    "title": "Normalization",
    "subtitle": "Organizing tables to reduce redundancy and update anomalies — the relational design baseline.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "Normalization splits data into tables so each fact lives in one place. Normal forms (1NF → 3NF, BCNF) eliminate insert/update/delete anomalies."
      },
      {
        "type": "table",
        "headers": [
          "Form",
          "Rule of thumb"
        ],
        "rows": [
          [
            "1NF",
            "Atomic columns, no repeating groups"
          ],
          [
            "2NF",
            "No partial dependency on composite key"
          ],
          [
            "3NF",
            "No transitive dependency on non-key columns"
          ],
          [
            "BCNF",
            "Every determinant is a candidate key"
          ]
        ]
      },
      {
        "type": "h",
        "text": "When to denormalize"
      },
      {
        "type": "ul",
        "items": [
          "Read-heavy dashboards — precomputed aggregates.",
          "Document stores embed related data — different trade-off.",
          "Always measure — joins with good indexes are often fast enough."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Give a concrete anomaly example: storing customer address on every order row → update customer address requires many row updates (update anomaly)."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Normalization show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Strong candidates connect Normalization to a real project story — prepare one example before the interview."
      }
    ],
    "references": [
      {
        "label": "PostgreSQL Documentation",
        "url": "https://www.postgresql.org/docs/"
      },
      {
        "label": "Use The Index, Luke",
        "url": "https://use-the-index-luke.com/"
      }
    ]
  },
  "indexes": {
    "title": "Indexes",
    "subtitle": "Data structures that speed up reads at the cost of write overhead and storage.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "Indexes (usually B-trees in Postgres/MySQL) let the DB find rows without full table scans. Primary key, unique, composite, and covering indexes serve different query patterns."
      },
      {
        "type": "ul",
        "items": [
          "Leftmost prefix rule for composite indexes (a,b) helps WHERE a=… and WHERE a=… AND b=….",
          "Covering index includes all SELECT columns — index-only scan.",
          "Writes must update indexes — too many indexes slow INSERT/UPDATE."
        ]
      },
      {
        "type": "code",
        "language": "sql",
        "title": "Composite index example",
        "code": "CREATE INDEX idx_orders_user_created\n  ON orders (user_id, created_at DESC);"
      },
      {
        "type": "callout",
        "tone": "interview",
        "title": "Explain EXPLAIN",
        "text": "Walk through seq scan vs index scan vs bitmap scan. Mention selectivity — low-cardinality columns alone make poor indexes."
      },
      {
        "type": "h",
        "text": "Index types beyond B-tree"
      },
      {
        "type": "ul",
        "items": [
          "Hash index — equality only, O(1) average (Memory engines).",
          "GIN/GiST — full-text and geospatial in Postgres.",
          "Partial index — index subset WHERE active = true saves space."
        ]
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Function on indexed column prevents index use: WHERE LOWER(email) = … unless functional index exists."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "PostgreSQL Documentation",
        "url": "https://www.postgresql.org/docs/"
      },
      {
        "label": "Use The Index, Luke",
        "url": "https://use-the-index-luke.com/"
      }
    ]
  },
  "transactions-and-acid": {
    "title": "Transactions & ACID",
    "subtitle": "Grouped operations that succeed or fail together — the foundation of reliable data stores.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "A transaction is a unit of work bounded by BEGIN/COMMIT/ROLLBACK. ACID: Atomicity (all or nothing), Consistency (valid state), Isolation (concurrent illusions), Durability (committed survives crash)."
      },
      {
        "type": "table",
        "headers": [
          "Property",
          "Meaning"
        ],
        "rows": [
          [
            "Atomicity",
            "Rollback on failure — no partial transfer"
          ],
          [
            "Consistency",
            "Constraints enforced — balances non-negative"
          ],
          [
            "Isolation",
            "Concurrent txs behave predictably"
          ],
          [
            "Durability",
            "WAL/fsync — commit outlives crash"
          ]
        ]
      },
      {
        "type": "code",
        "language": "sql",
        "code": "BEGIN;\nUPDATE accounts SET balance = balance - 100 WHERE id = 1;\nUPDATE accounts SET balance = balance + 100 WHERE id = 2;\nCOMMIT;"
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Distributed transactions (2PC) are avoided in microservices — sagas or idempotent outbox patterns instead."
      },
      {
        "type": "h",
        "text": "Logging and durability"
      },
      {
        "type": "p",
        "text": "Write-Ahead Log (WAL) records changes before applying to data pages — COMMIT waits for WAL fsync (depending on synchronous_commit settings)."
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Idempotent retries + unique constraints give practical atomicity across services without 2PC."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "CAP at DB level: single-node ACID vs distributed eventual consistency — know where your DB sits."
      }
    ],
    "references": [
      {
        "label": "PostgreSQL Documentation",
        "url": "https://www.postgresql.org/docs/"
      },
      {
        "label": "Use The Index, Luke",
        "url": "https://use-the-index-luke.com/"
      }
    ]
  },
  "mvcc": {
    "title": "MVCC",
    "subtitle": "Multi-Version Concurrency Control — readers don't block writers by keeping row versions.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "MVCC stores multiple versions of a row. Readers see a snapshot as of transaction start; writers create new versions. Postgres and InnoDB use MVCC variants."
      },
      {
        "type": "ul",
        "items": [
          "Readers avoid blocking writers — good OLTP throughput.",
          "Old versions need vacuum/undo cleanup — bloat if neglected.",
          "Snapshot isolation prevents dirty reads; anomalies depend on isolation level."
        ]
      },
      {
        "type": "diagram",
        "kind": "mvcc-snapshot",
        "caption": "Each transaction reads visible row versions per its snapshot timestamp."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Contrast with lock-based reads (SELECT FOR UPDATE). MVCC is why long-running reports can see old data without blocking writes."
      },
      {
        "type": "h",
        "text": "Vacuum and bloat"
      },
      {
        "type": "p",
        "text": "Postgres VACUUM reclaims dead tuple space. Long transactions block vacuum → table bloat → slower scans. Monitor autovacuum lag."
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Serializable isolation in Postgres uses SSI — different implementation details than MySQL — read vendor docs."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Snapshot too old errors in Oracle/long queries — classic MVCC operational issue."
      }
    ],
    "references": [
      {
        "label": "PostgreSQL Documentation",
        "url": "https://www.postgresql.org/docs/"
      },
      {
        "label": "Use The Index, Luke",
        "url": "https://use-the-index-luke.com/"
      }
    ]
  },
  "isolation-levels": {
    "title": "Isolation Levels",
    "subtitle": "How much concurrent transaction interference you allow for performance.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "SQL defines isolation levels with trade-offs between consistency and concurrency. Anomalies: dirty read, non-repeatable read, phantom read."
      },
      {
        "type": "table",
        "headers": [
          "Level",
          "Typical anomalies prevented"
        ],
        "rows": [
          [
            "READ UNCOMMITTED",
            "Almost none — rarely used"
          ],
          [
            "READ COMMITTED",
            "Dirty reads — default in Postgres"
          ],
          [
            "REPEATABLE READ",
            "Non-repeatable reads — MySQL default"
          ],
          [
            "SERIALIZABLE",
            "Phantoms — slowest, safest"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Repeatable read in Postgres still prevents phantom reads via MVCC — DB implementations differ from standard wording."
      },
      {
        "type": "callout",
        "tone": "interview",
        "title": "Backend choice",
        "text": "Payment ledger: SERIALIZABLE or explicit row locks. Analytics: READ COMMITTED or replica."
      },
      {
        "type": "h",
        "text": "Choosing a level in backend code"
      },
      {
        "type": "table",
        "headers": [
          "Use case",
          "Level"
        ],
        "rows": [
          [
            "Read-mostly API",
            "READ COMMITTED"
          ],
          [
            "Report within one snapshot",
            "REPEATABLE READ or replica"
          ],
          [
            "Financial invariant",
            "SERIALIZABLE or explicit locks"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Default level differs by DB — never assume READ COMMITTED everywhere without checking."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "PostgreSQL Documentation",
        "url": "https://www.postgresql.org/docs/"
      },
      {
        "label": "Use The Index, Luke",
        "url": "https://use-the-index-luke.com/"
      }
    ]
  },
  "locks": {
    "title": "Locks",
    "subtitle": "Coordinating access in databases and application code — from row locks to mutexes.",
    "readingMinutes": 11,
    "blocks": [
      {
        "type": "p",
        "text": "Locks appear in both DBMS (pessimistic row/table locks) and concurrent programs (mutexes, synchronized blocks). The goal is the same: prevent conflicting simultaneous access."
      },
      {
        "type": "h",
        "text": "Database locks"
      },
      {
        "type": "ul",
        "items": [
          "Shared (S) for reads, exclusive (X) for writes — compatibility matrix matters.",
          "SELECT FOR UPDATE takes row locks until transaction ends.",
          "Deadlock detection picks a victim transaction to rollback.",
          "Optimistic locking: version column — UPDATE … WHERE id=? AND version=?"
        ]
      },
      {
        "type": "h",
        "text": "Application / concurrency locks"
      },
      {
        "type": "ul",
        "items": [
          "Mutex / ReentrantLock — one writer to in-memory structure.",
          "Read-write locks — many readers OR one writer.",
          "Distributed locks (Redis Redlock, DB advisory locks) — coordination across services with expiry and fencing tokens."
        ]
      },
      {
        "type": "table",
        "headers": [
          "Mechanism",
          "Scope",
          "Best for"
        ],
        "rows": [
          [
            "Row lock",
            "Single DB transaction",
            "Financial debit/credit"
          ],
          [
            "App mutex",
            "Single process",
            "In-memory cache map"
          ],
          [
            "Distributed lock",
            "Cluster",
            "Leader election, cron singleton"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "One article covers both: explain when DB isolation is enough vs when you need explicit app-level or distributed locks."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "PostgreSQL Documentation",
        "url": "https://www.postgresql.org/docs/"
      },
      {
        "label": "Use The Index, Luke",
        "url": "https://use-the-index-luke.com/"
      }
    ]
  },
  "joins": {
    "title": "Joins",
    "subtitle": "Combining rows from related tables — the heart of relational queries.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "JOINs correlate tables on keys. INNER keeps matches only; LEFT keeps all left rows; OUTER variants include non-matching rows with NULLs."
      },
      {
        "type": "table",
        "headers": [
          "Join type",
          "Result"
        ],
        "rows": [
          [
            "INNER",
            "Matching rows from both sides"
          ],
          [
            "LEFT",
            "All left + matched right or NULL"
          ],
          [
            "CROSS",
            "Cartesian product — usually accidental"
          ],
          [
            "SELF",
            "Row joined to another row same table"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Ensure join columns are indexed. Nested loop vs hash join vs merge join — check EXPLAIN for large tables."
      },
      {
        "type": "code",
        "language": "sql",
        "code": "SELECT u.name, o.total\nFROM users u\nINNER JOIN orders o ON o.user_id = u.id\nWHERE o.created_at > NOW() - INTERVAL '7 days';"
      },
      {
        "type": "h",
        "text": "Join algorithms (EXPLAIN)"
      },
      {
        "type": "ul",
        "items": [
          "Nested loop — small outer, index on inner.",
          "Hash join — equi-join on large tables, builds hash table.",
          "Merge join — both sides sorted on join key."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "N+1 query problem: ORM lazy loads in loop — fix with JOIN or batch IN query."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "PostgreSQL Documentation",
        "url": "https://www.postgresql.org/docs/"
      },
      {
        "label": "Use The Index, Luke",
        "url": "https://use-the-index-luke.com/"
      }
    ]
  },
  "execution-plans": {
    "title": "Execution Plans",
    "subtitle": "How the query optimizer actually runs your SQL — essential for performance debugging.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "EXPLAIN (ANALYZE, BUFFERS) shows scan types, join methods, row estimates vs actuals, and cost. Bad plans mean missing indexes or stale statistics."
      },
      {
        "type": "steps",
        "items": [
          {
            "title": "Run EXPLAIN ANALYZE",
            "text": "On production-like data volume — dev with 100 rows lies."
          },
          {
            "title": "Check seq scans",
            "text": "Large tables scanning end-to-end → index candidate."
          },
          {
            "title": "Compare rows estimated vs actual",
            "text": "Huge gap → run ANALYZE, fix correlated predicates."
          },
          {
            "title": "Iterate",
            "text": "Add index, rewrite query, or materialized view."
          }
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Story: p99 latency fixed by composite index after EXPLAIN showed seq scan on 50M row table."
      },
      {
        "type": "h",
        "text": "Red flags in plans"
      },
      {
        "type": "ul",
        "items": [
          "Seq Scan on large table with high filter ratio.",
          "Nested Loop with high row estimate on inner side.",
          "Sort + HashAggregate spilling to disk (external sort)."
        ]
      },
      {
        "type": "code",
        "language": "sql",
        "code": "EXPLAIN (ANALYZE, BUFFERS, FORMAT TEXT)\nSELECT * FROM orders WHERE user_id = 42 AND status = 'open';"
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "ANALYZE after large data migrations — stale stats cause catastrophic plan choices."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "PostgreSQL Documentation",
        "url": "https://www.postgresql.org/docs/"
      },
      {
        "label": "Use The Index, Luke",
        "url": "https://use-the-index-luke.com/"
      }
    ]
  },
  "connection-pooling": {
    "title": "Connection Pooling",
    "subtitle": "Reuse DB connections instead of opening a new TCP+auth handshake per request.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "Each DB connection costs memory on server and file descriptors. Pools (HikariCP, pgBouncer) maintain warm connections borrowed by app threads."
      },
      {
        "type": "ul",
        "items": [
          "Size pool from concurrent queries, not thread count — async apps need fewer.",
          "Pool exhaustion looks like timeouts under load — monitor active/waiting.",
          "Transaction-level pooling (pgBouncer) vs session-level — prepared statements differ."
        ]
      },
      {
        "type": "formula",
        "latex": "pool_size ≈ (core_count × 2) + effective_spindle_count",
        "meaning": "Classic starting heuristic — tune with metrics, not gospel."
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Leaked connections (missing close in finally) drain the pool — always use try-with-resources."
      },
      {
        "type": "h",
        "text": "Common production mistakes"
      },
      {
        "type": "ul",
        "items": [
          "Pool size = thread count on 500-thread app → DB melts.",
          "Long transactions hold connections from pool.",
          "PgBouncer transaction pooling breaks session-level features (temp tables, SET)."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Explain waiting thread blocked on pool.getConnection() — metric: pool pending connections."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "PostgreSQL Documentation",
        "url": "https://www.postgresql.org/docs/"
      },
      {
        "label": "Use The Index, Luke",
        "url": "https://use-the-index-luke.com/"
      }
    ]
  },
  "tcp": {
    "title": "TCP",
    "subtitle": "Reliable, ordered byte stream between two hosts — the transport of HTTP, SQL, and most backend traffic.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "TCP sets up a connection (3-way handshake), numbers segments for ordering, retransmits on loss, and flow-controls with window size."
      },
      {
        "type": "diagram",
        "kind": "tcp-handshake",
        "caption": "SYN → SYN-ACK → ACK establishes connection; FIN closes gracefully."
      },
      {
        "type": "table",
        "headers": [
          "Feature",
          "Benefit",
          "Cost"
        ],
        "rows": [
          [
            "Retransmission",
            "Reliability",
            "Latency on loss"
          ],
          [
            "Ordering",
            "Correct stream",
            "Buffering"
          ],
          [
            "Congestion control",
            "Network stability",
            "Variable throughput"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "TCP slow start affects cold connections — why connection pooling and keep-alive matter for latency."
      },
      {
        "type": "h",
        "text": "TCP tuning awareness"
      },
      {
        "type": "ul",
        "items": [
          "Nagle algorithm — small packet delay; TCP_NODELAY for low latency.",
          "Keep-alive probes detect dead peers.",
          "TIME_WAIT accumulation on high-churn clients — reuse ports or tune tw_reuse carefully."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Why is TLS handshake expensive? TCP handshake + cert verify + key exchange — HTTP/2 and HTTP/3 reduce round trips."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "Cloudflare Learning Center",
        "url": "https://www.cloudflare.com/learning/"
      },
      {
        "label": "MDN HTTP",
        "url": "https://developer.mozilla.org/en-US/docs/Web/HTTP"
      }
    ]
  },
  "udp": {
    "title": "UDP",
    "subtitle": "Unreliable datagrams — low overhead when you can tolerate or handle loss yourself.",
    "readingMinutes": 8,
    "blocks": [
      {
        "type": "p",
        "text": "UDP sends self-contained packets with no guaranteed delivery, order, or congestion control. Used for DNS, gaming, VoIP, and QUIC (built on UDP)."
      },
      {
        "type": "ul",
        "items": [
          "Lower latency — no handshake for first datagram.",
          "Application must handle loss/reorder if needed.",
          "Broadcast/multicast possible (where supported)."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "title": "When to pick UDP",
        "text": "Real-time media, DNS queries, custom protocols with app-level ACK — not money transfers."
      },
      {
        "type": "h",
        "text": "QUIC on UDP"
      },
      {
        "type": "p",
        "text": "HTTP/3 runs over QUIC which implements reliability, encryption, and multiplexing in user space on UDP — avoids TCP head-of-line blocking for web."
      },
      {
        "type": "table",
        "headers": [
          "Protocol",
          "Reliability"
        ],
        "rows": [
          [
            "TCP",
            "Kernel retransmission"
          ],
          [
            "UDP",
            "None — app handles if needed"
          ],
          [
            "QUIC/UDP",
            "User-space streams with retransmission"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "DNS uses UDP for small queries; falls back to TCP for large responses (DNSSEC)."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "Cloudflare Learning Center",
        "url": "https://www.cloudflare.com/learning/"
      },
      {
        "label": "MDN HTTP",
        "url": "https://developer.mozilla.org/en-US/docs/Web/HTTP"
      }
    ]
  },
  "http-http-2-http-3": {
    "title": "HTTP / HTTP/2 / HTTP/3",
    "subtitle": "How browsers and APIs request resources — from plain text to multiplexed streams over QUIC.",
    "readingMinutes": 11,
    "blocks": [
      {
        "type": "p",
        "text": "HTTP/1.1: text headers, one request per connection unless keep-alive pipelining (limited). HTTP/2: binary framing, multiplexed streams, header compression (HPACK). HTTP/3: same semantics over QUIC (UDP + TLS 1.3 + built-in multiplexing)."
      },
      {
        "type": "table",
        "headers": [
          "Version",
          "Transport",
          "Key win"
        ],
        "rows": [
          [
            "HTTP/1.1",
            "TCP",
            "Simple, universal"
          ],
          [
            "HTTP/2",
            "TCP",
            "Multiplexing — fixes head-of-line blocking at HTTP layer"
          ],
          [
            "HTTP/3",
            "QUIC/UDP",
            "Better loss recovery; faster handshakes"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Head-of-line blocking still exists in HTTP/2 at TCP layer — one lost packet stalls all streams on that connection."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Methods: GET safe/idempotent, POST creates, PUT replaces, PATCH partial, DELETE removes. Status codes: 2xx success, 4xx client, 5xx server."
      },
      {
        "type": "h",
        "text": "Practical backend implications"
      },
      {
        "type": "ul",
        "items": [
          "HTTP/2 server push largely deprecated — prefer preload hints.",
          "GRPC requires HTTP/2 — load balancers must support it.",
          "HTTP/3 needs UDP pass-through — some corporate firewalls block it."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Idempotency-Key header on POST enables safe retries — related to HTTP semantics, not version."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "Cloudflare Learning Center",
        "url": "https://www.cloudflare.com/learning/"
      },
      {
        "label": "MDN HTTP",
        "url": "https://developer.mozilla.org/en-US/docs/Web/HTTP"
      }
    ]
  },
  "https": {
    "title": "HTTPS",
    "subtitle": "HTTP over TLS — encryption and identity verification for data in transit.",
    "readingMinutes": 8,
    "blocks": [
      {
        "type": "p",
        "text": "HTTPS wraps HTTP in TLS: confidentiality, integrity, and (with valid certs) server authentication. Browsers require it for modern features."
      },
      {
        "type": "steps",
        "items": [
          {
            "title": "TLS handshake",
            "text": "Negotiate cipher suite, exchange keys (often ECDHE)."
          },
          {
            "title": "Certificate",
            "text": "Server presents chain; client validates against trust store."
          },
          {
            "title": "Encrypted HTTP",
            "text": "Application data flows inside TLS records."
          }
        ]
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "HTTPS encrypts transit, not storage — still hash passwords and protect data at rest."
      },
      {
        "type": "h",
        "text": "Certificate lifecycle"
      },
      {
        "type": "ul",
        "items": [
          "Let's Encrypt automates 90-day certs — cert-manager in K8s.",
          "Mixed content: HTTPS page loading HTTP assets breaks browser security.",
          "HSTS header forces HTTPS after first visit."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Explain mTLS for service mesh — both sides present certificates for zero-trust internal traffic."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does HTTPS show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Cloudflare Learning Center",
        "url": "https://www.cloudflare.com/learning/"
      },
      {
        "label": "MDN HTTP",
        "url": "https://developer.mozilla.org/en-US/docs/Web/HTTP"
      }
    ]
  },
  "dns": {
    "title": "DNS",
    "subtitle": "The phone book of the internet — maps hostnames to IP addresses and more.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "DNS resolves names hierarchically (root → TLD → authoritative). Records: A/AAAA (IP), CNAME (alias), MX (mail), TXT (verification), SRV, etc."
      },
      {
        "type": "ul",
        "items": [
          "Caching at OS, browser, and resolver reduces latency.",
          "TTL controls cache freshness vs load on authoritative servers.",
          "Failures: typo domains, expired TTL during migration, split-horizon in corp networks."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Trace resolution: browser cache → OS → recursive resolver → root → TLD → authoritative."
      },
      {
        "type": "h",
        "text": "Backend debugging scenarios"
      },
      {
        "type": "ol",
        "items": [
          "Service moved — stale cache until TTL expires.",
          "Split DNS: internal vs external resolution differs.",
          "CNAME chains add latency — flatten where possible."
        ]
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Hardcode IP instead of DNS in config breaks failover — always use hostnames with health-checked DNS."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does DNS show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Cloudflare Learning Center",
        "url": "https://www.cloudflare.com/learning/"
      },
      {
        "label": "MDN HTTP",
        "url": "https://developer.mozilla.org/en-US/docs/Web/HTTP"
      }
    ]
  },
  "tls": {
    "title": "TLS",
    "subtitle": "Transport Layer Security — the protocol behind HTTPS and secure gRPC.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "TLS 1.3 is current best practice: fewer round trips, modern ciphers only, forward secrecy with ephemeral keys."
      },
      {
        "type": "table",
        "headers": [
          "Concept",
          "Purpose"
        ],
        "rows": [
          [
            "Certificate",
            "Bind public key to domain identity"
          ],
          [
            "Cipher suite",
            "Algorithms for key exchange + encryption + MAC"
          ],
          [
            "SNI",
            "Multiple HTTPS sites on one IP"
          ],
          [
            "mTLS",
            "Client also presents cert — service mesh"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Terminate TLS at load balancer vs app — central cert management vs end-to-end encryption trade-off."
      },
      {
        "type": "h",
        "text": "TLS termination patterns"
      },
      {
        "type": "ul",
        "items": [
          "Edge termination: LB decrypts, HTTP inside VPC (trust boundary matters).",
          "Pass-through: LB routes TCP 443, app terminates TLS.",
          "Mutual TLS: client cert required for admin APIs."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Forward secrecy: ephemeral DH keys mean captured ciphertext cannot be decrypted later if long-term key leaks."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does TLS show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Cloudflare Learning Center",
        "url": "https://www.cloudflare.com/learning/"
      },
      {
        "label": "MDN HTTP",
        "url": "https://developer.mozilla.org/en-US/docs/Web/HTTP"
      }
    ]
  },
  "rest": {
    "title": "REST",
    "subtitle": "Representational State Transfer — HTTP APIs modeled as resources with standard verbs.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "RESTful APIs expose nouns (resources) at URLs, use HTTP methods semantically, and return representations (usually JSON). Stateless servers scale horizontally."
      },
      {
        "type": "ul",
        "items": [
          "Version in URL (/v1/) or header — be consistent.",
          "Pagination: cursor-based for live feeds, offset for admin UIs.",
          "Errors: problem+json or consistent { code, message, details } shape.",
          "HATEOAS optional — most APIs skip hypermedia links in practice."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "REST vs RPC: REST caches well with GET; RPC-style POST /createOrder is fine internally but weaker HTTP semantics."
      },
      {
        "type": "h",
        "text": "API design checklist"
      },
      {
        "type": "ol",
        "items": [
          "Nouns in URLs, verbs in HTTP methods.",
          "Consistent error schema and correlation IDs.",
          "Pagination, filtering, sorting documented in OpenAPI.",
          "Rate limits and auth documented — 401 vs 403 correctly."
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "OpenAPI/Swagger generates client SDKs — reduces integration bugs."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does REST show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Cloudflare Learning Center",
        "url": "https://www.cloudflare.com/learning/"
      },
      {
        "label": "MDN HTTP",
        "url": "https://developer.mozilla.org/en-US/docs/Web/HTTP"
      }
    ]
  },
  "grpc": {
    "title": "gRPC",
    "subtitle": "High-performance RPC over HTTP/2 with Protocol Buffers — common inside microservice meshes.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "gRPC uses .proto contracts, binary Protobuf payloads, streaming (unary, server, client, bidirectional), and HTTP/2 multiplexing."
      },
      {
        "type": "table",
        "headers": [
          "",
          "REST/JSON",
          "gRPC/Protobuf"
        ],
        "rows": [
          [
            "Contract",
            "OpenAPI optional",
            "Required .proto"
          ],
          [
            "Payload",
            "Human-readable",
            "Compact binary"
          ],
          [
            "Browser",
            "Native",
            "Needs grpc-web gateway"
          ],
          [
            "Streaming",
            "SSE/WebSocket separate",
            "Built-in"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Pick gRPC for internal low-latency service-to-service; REST for public third-party APIs."
      },
      {
        "type": "h",
        "text": "Implementation notes"
      },
      {
        "type": "ul",
        "items": [
          "Deadline/timeout propagated in metadata — always set.",
          "Status codes map from gRPC to HTTP in grpc-gateway.",
          "Protobuf backward compatibility: never reuse field numbers."
        ]
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Breaking proto changes require careful rollout — use field presence and versioned services."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does gRPC show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Cloudflare Learning Center",
        "url": "https://www.cloudflare.com/learning/"
      },
      {
        "label": "MDN HTTP",
        "url": "https://developer.mozilla.org/en-US/docs/Web/HTTP"
      }
    ]
  },
  "websocket": {
    "title": "WebSocket",
    "subtitle": "Full-duplex persistent connection over a single TCP socket — push without polling.",
    "readingMinutes": 8,
    "blocks": [
      {
        "type": "p",
        "text": "WebSocket upgrades HTTP connection to framed bidirectional messages. Used for chat, live dashboards, collaborative editing."
      },
      {
        "type": "ul",
        "items": [
          "Starts with HTTP Upgrade handshake.",
          "Server can push without client polling — lower latency and load.",
          "Scale with sticky sessions or pub/sub backplane (Redis) across instances."
        ]
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Long-lived connections consume memory and file descriptors — cap per instance and use heartbeats."
      },
      {
        "type": "h",
        "text": "Scaling pattern"
      },
      {
        "type": "ol",
        "items": [
          "Client connects to instance A via sticky load balancer.",
          "Publish events to Redis Pub/Sub or Kafka.",
          "All instances subscribe; local connections receive push."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Compare SSE (one-way server push over HTTP) vs WebSocket (bidirectional) — simpler ops for read-only feeds."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does WebSocket show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Cloudflare Learning Center",
        "url": "https://www.cloudflare.com/learning/"
      },
      {
        "label": "MDN HTTP",
        "url": "https://developer.mozilla.org/en-US/docs/Web/HTTP"
      }
    ]
  },
  "essential-commands": {
    "title": "Essential Commands",
    "subtitle": "Linux CLI tools every backend engineer uses daily for debugging and ops.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "You cannot ssh into production without comfort in basic commands. These diagnose CPU, memory, disk, network, and processes."
      },
      {
        "type": "table",
        "headers": [
          "Command",
          "Use"
        ],
        "rows": [
          [
            "ls, cd, pwd, cp, mv, rm",
            "Navigation and files"
          ],
          [
            "grep, awk, sed, tail -f",
            "Log analysis"
          ],
          [
            "curl, wget",
            "HTTP probing"
          ],
          [
            "ssh, scp",
            "Remote access and copy"
          ],
          [
            "chmod, chown",
            "Permissions"
          ],
          [
            "tar, gzip",
            "Archives"
          ]
        ]
      },
      {
        "type": "code",
        "language": "bash",
        "title": "Quick health check",
        "code": "uptime\ndf -h\nfree -h\nss -tlnp | head"
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Walk through debugging high CPU: top/htop → identify PID → strace or perf → read logs."
      },
      {
        "type": "h",
        "text": "Permissions quick reference"
      },
      {
        "type": "p",
        "text": "chmod 755: rwx for owner, rx for group/other. chown user:group file. sudo for elevated ops — audit who has prod sudo."
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "rm -rf with wrong path is a career-limiting move — use trash-cli or double-check tab completion."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "find /var/log -name \"*.log\" -mtime +7 — demonstrate safe log cleanup thinking."
      }
    ],
    "references": [
      {
        "label": "Linux man pages",
        "url": "https://man7.org/linux/man-pages/"
      }
    ]
  },
  "memory": {
    "title": "Memory",
    "subtitle": "How Linux tracks RAM usage — and why your Java heap is not the whole story.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "Process memory includes heap, stack, code, mmap'd files, and shared libs. OS uses page cache for file I/O — \"used\" RAM is often cache, not waste."
      },
      {
        "type": "ul",
        "items": [
          "RSS vs VSZ: resident vs virtual size.",
          "OOM killer scores processes when overcommit fails.",
          "Containers: cgroup memory limits trigger kill before host OOM."
        ]
      },
      {
        "type": "code",
        "language": "bash",
        "code": "free -h\nps aux --sort=-%mem | head\nvmstat 1"
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Java \"memory leak\" may be heap — use jmap/jcmd heap dump and MAT analysis."
      },
      {
        "type": "h",
        "text": "Container memory limits"
      },
      {
        "type": "p",
        "text": "Kubernetes limits trigger OOMKill at cgroup boundary — JVM must use -XX:MaxRAMPercentage or container-aware defaults or it over-allocates heap and gets killed."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Explain difference between memory leak (unbounded growth) and memory pressure (legitimate cache that needs tuning)."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "Linux man pages",
        "url": "https://man7.org/linux/man-pages/"
      }
    ]
  },
  "cpu": {
    "title": "CPU",
    "subtitle": "Finding what burns CPU cycles on a Linux server.",
    "readingMinutes": 8,
    "blocks": [
      {
        "type": "p",
        "text": "High CPU may be user (app code), system (syscalls), iowait (disk bottleneck), or steal (noisy neighbor in VMs)."
      },
      {
        "type": "ul",
        "items": [
          "top/htop — sort by CPU, press H for threads.",
          "perf record/report — flame graphs in production (careful overhead).",
          "pidstat -u 1 — per-process CPU over time."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Distinguish CPU-bound (need more cores/optimization) vs I/O-bound (threads idle waiting — profile wrong if you only optimize CPU)."
      },
      {
        "type": "h",
        "text": "Load average interpretation"
      },
      {
        "type": "p",
        "text": "load average 4.0 on 4 cores ≈ saturated. load 8 on 4 cores means runnable queue is twice core count — investigate."
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "strace -p PID -c summarizes syscalls — find excessive stat/open calls."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Profiling beats guessing: one flame graph finding beats hours of speculative optimization."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "Linux man pages",
        "url": "https://man7.org/linux/man-pages/"
      }
    ]
  },
  "disk-i-o": {
    "title": "Disk I/O",
    "subtitle": "Storage latency often dominates p99 — know how to spot and fix I/O bottlenecks.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "Disk I/O includes random vs sequential reads/writes, fsync for durability, and SSD vs HDD orders-of-magnitude difference."
      },
      {
        "type": "ul",
        "items": [
          "iostat -xz 1 — util% near 100% means disk saturated.",
          "iotop — which process reads/writes.",
          "DB: WAL fsync policy trades durability vs speed."
        ]
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Logging synchronously to disk on every request destroys throughput — batch or async appenders."
      },
      {
        "type": "h",
        "text": "Application-level I/O patterns"
      },
      {
        "type": "ul",
        "items": [
          "Batch writes instead of fsync per event.",
          "Use append-only logs for durability (Kafka, WAL).",
          "SSD wear and IOPS limits in cloud — pick right volume type."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "iowait high but disk util low → NFS or network storage latency; not local disk saturation."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Disk I/O show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Linux man pages",
        "url": "https://man7.org/linux/man-pages/"
      }
    ]
  },
  "process-management": {
    "title": "Process Management",
    "subtitle": "Inspecting, signaling, and controlling processes on Linux.",
    "readingMinutes": 8,
    "blocks": [
      {
        "type": "p",
        "text": "Every running program is a process with PID, parent, niceness, and file descriptors. systemd manages services on modern distros."
      },
      {
        "type": "table",
        "headers": [
          "Command",
          "Action"
        ],
        "rows": [
          [
            "ps aux / ps -ef",
            "List processes"
          ],
          [
            "kill -15 PID",
            "SIGTERM graceful shutdown"
          ],
          [
            "kill -9 PID",
            "SIGKILL force — last resort"
          ],
          [
            "nice / renice",
            "Adjust priority"
          ],
          [
            "systemctl status svc",
            "Service health"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Zombie processes: child exited but parent never wait() — fix parent, not zombie PID."
      },
      {
        "type": "h",
        "text": "Signals every backend dev should know"
      },
      {
        "type": "table",
        "headers": [
          "Signal",
          "Default action"
        ],
        "rows": [
          [
            "SIGTERM (15)",
            "Graceful shutdown request"
          ],
          [
            "SIGKILL (9)",
            "Immediate kill — cannot catch"
          ],
          [
            "SIGHUP",
            "Reload config (daemons)"
          ],
          [
            "SIGINT (2)",
            "Interrupt — Ctrl+C"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Kubernetes preStop hook + SIGTERM handler with drain period — graceful shutdown interview classic."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Process Management show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Linux man pages",
        "url": "https://man7.org/linux/man-pages/"
      }
    ]
  },
  "networking-tools": {
    "title": "Networking Tools",
    "subtitle": "CLI tools to debug connectivity, DNS, and latency from the shell.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "When \"it works on my machine\" fails in prod, these tools isolate DNS, routing, firewall, and TLS issues."
      },
      {
        "type": "ul",
        "items": [
          "ping / ping6 — ICMP reachability (may be blocked).",
          "traceroute / mtr — path and per-hop latency.",
          "dig / nslookup — DNS answers from resolvers.",
          "ss -tlnp / netstat — listening ports and connections.",
          "tcpdump / wireshark — packet capture (permission sensitive).",
          "openssl s_client — inspect TLS cert chain."
        ]
      },
      {
        "type": "code",
        "language": "bash",
        "code": "dig +short api.example.com\ncurl -vI https://api.example.com/health\nss -tan state established | wc -l"
      },
      {
        "type": "h",
        "text": "Debugging flow"
      },
      {
        "type": "steps",
        "items": [
          {
            "title": "DNS",
            "text": "dig resolves name?"
          },
          {
            "title": "TCP",
            "text": "nc -zv host port or curl -v"
          },
          {
            "title": "TLS",
            "text": "openssl s_client -connect host:443"
          },
          {
            "title": "Path",
            "text": "mtr for packet loss hop"
          }
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Corporate VPN split tunneling causes \"works locally, fails in office\" — compare dig from both networks."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Networking Tools show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Linux man pages",
        "url": "https://man7.org/linux/man-pages/"
      }
    ]
  },
  "thread-pool": {
    "title": "Thread Pool",
    "subtitle": "Bounded worker threads that reuse execution resources instead of unbounded thread creation.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "Thread pools queue tasks and assign them to fixed (or elastic) worker threads. They cap memory and scheduler overhead while exploiting multiple cores."
      },
      {
        "type": "ul",
        "items": [
          "Size for CPU work ≈ number of cores; for I/O work can be higher but still bounded.",
          "Queue depth + rejection policy (abort, caller-runs, discard) define backpressure.",
          "ForkJoinPool for divide-and-conquer; ExecutorService for general tasks."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Too few threads → underutilized CPU; too many → context switch overhead and memory — tune with metrics."
      },
      {
        "type": "code",
        "language": "java",
        "code": "ThreadPoolExecutor ex = new ThreadPoolExecutor(\n  8, 16, 60L, TimeUnit.SECONDS,\n  new ArrayBlockingQueue<>(1000),\n  new ThreadPoolExecutor.CallerRunsPolicy());"
      },
      {
        "type": "h",
        "text": "Sizing worked example"
      },
      {
        "type": "p",
        "text": "8-core server, I/O-bound tasks wait 90% of time: pool of 80 may keep CPUs busy (Little's Law intuition). CPU-bound: pool ≈ cores."
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Unbounded queue + fixed threads hides overload until OOM — use bounded queue and rejection policy for backpressure."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "OSTEP — Operating Systems",
        "url": "https://pages.cs.wisc.edu/~remzi/OSTEP/"
      }
    ]
  },
  "executors": {
    "title": "Executors",
    "subtitle": "Higher-level APIs that submit tasks without manual Thread lifecycle management.",
    "readingMinutes": 8,
    "blocks": [
      {
        "type": "p",
        "text": "Executor frameworks decouple task submission from execution thread management — Runnable/Callable, CompletableFuture, scheduled tasks."
      },
      {
        "type": "ul",
        "items": [
          "Executors.newFixedThreadPool — constant workers.",
          "ScheduledExecutorService — cron-like delays.",
          "CompletableFuture — compose async pipelines with thenApply/allOf.",
          "Virtual threads (Java 21+) — cheap threads for I/O-heavy workloads."
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Always shut down executors on app stop — awaitTermination with timeout prevents hung JVM."
      },
      {
        "type": "h",
        "text": "CompletableFuture patterns"
      },
      {
        "type": "code",
        "language": "java",
        "code": "CompletableFuture.supplyAsync(() -> fetchUser(id), pool)\n  .thenCompose(u -> fetchOrders(u))\n  .exceptionally(ex -> fallback);"
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Explain difference between submit() returning Future vs CompletableFuture composition for pipeline stages."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Executors show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "OSTEP — Operating Systems",
        "url": "https://pages.cs.wisc.edu/~remzi/OSTEP/"
      }
    ]
  },
  "synchronization": {
    "title": "Synchronization",
    "subtitle": "Coordinating threads so shared state stays correct under parallel access.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "Synchronization tools: locks, semaphores, condition variables, barriers, atomic variables, and lock-free structures."
      },
      {
        "type": "table",
        "headers": [
          "Tool",
          "When"
        ],
        "rows": [
          [
            "synchronized / mutex",
            "Simple critical sections"
          ],
          [
            "ReadWriteLock",
            "Many readers, rare writers"
          ],
          [
            "AtomicInteger",
            "Single variable counters"
          ],
          [
            "BlockingQueue",
            "Producer-consumer handoff"
          ],
          [
            "CountDownLatch",
            "Wait for N tasks to finish"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Prefer immutability and message passing where possible — less locking means fewer deadlocks."
      },
      {
        "type": "h",
        "text": "Lock-free snapshot"
      },
      {
        "type": "p",
        "text": "AtomicReference.compareAndSet enables lock-free stacks/queues for hot paths — harder to verify correct than locks."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "volatile in Java guarantees visibility, not atomicity — i++ still races."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Synchronization show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "OSTEP — Operating Systems",
        "url": "https://pages.cs.wisc.edu/~remzi/OSTEP/"
      }
    ]
  },
  "async-await": {
    "title": "Async / Await",
    "subtitle": "Non-blocking style that looks synchronous — frees threads while waiting on I/O.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "async/await (C#, JS, Python asyncio) suspends coroutines at await points without blocking OS threads. The runtime resumes when I/O completes."
      },
      {
        "type": "ul",
        "items": [
          "Great for I/O-bound web servers and API gateways.",
          "Does not speed CPU-bound work — still need threads/processes for parallelism.",
          "Watch blocking calls inside async code — they stall the event loop."
        ]
      },
      {
        "type": "code",
        "language": "javascript",
        "title": "Node.js async handler",
        "code": "app.get(\"/user/:id\", async (req, res) => {\n  const user = await db.findUser(req.params.id);\n  res.json(user);\n});"
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "async ≠ parallel. Interviewers ask: what happens if you forget await? Promise object returned, race bugs."
      },
      {
        "type": "h",
        "text": "Event loop mental model (Node.js)"
      },
      {
        "type": "p",
        "text": "Single thread runs JS; libuv thread pool handles some I/O. CPU work blocks everything — offload to worker threads."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Compare cooperative multitasking (async) vs preemptive (OS threads) — fairness and starvation trade-offs."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "OSTEP — Operating Systems",
        "url": "https://pages.cs.wisc.edu/~remzi/OSTEP/"
      }
    ]
  },
  "race-conditions": {
    "title": "Race Conditions",
    "subtitle": "When outcome depends on unpredictable thread interleaving — subtle production bugs.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "A race occurs when two threads access shared mutable state, at least one writes, and synchronization is missing. Symptoms: flaky tests, rare duplicate charges, corrupted counters."
      },
      {
        "type": "h",
        "text": "Classic example: check-then-act"
      },
      {
        "type": "code",
        "language": "java",
        "code": "if (balance >= amount) {  // another thread may withdraw here\n  balance -= amount;\n}"
      },
      {
        "type": "ul",
        "items": [
          "Fix with atomic operations, locks, or database constraints (UNIQUE, SERIALIZABLE).",
          "Stress tests and Thread Sanitizer (TSan) help reproduce.",
          "Idempotency keys prevent duplicate side effects even if retried."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Tell a story: intermittent double booking fixed by UNIQUE constraint + transactional outbox."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Race Conditions show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Strong candidates connect Race Conditions to a real project story — prepare one example before the interview."
      }
    ],
    "references": [
      {
        "label": "OSTEP — Operating Systems",
        "url": "https://pages.cs.wisc.edu/~remzi/OSTEP/"
      }
    ]
  },
  "actors-and-message-passing": {
    "title": "Actors & Message Passing",
    "subtitle": "Concurrency without shared mutable state — each actor processes mailboxes sequentially.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "Actor models (Erlang, Akka) encapsulate state; communication is async messages. No shared locks inside actor — avoids many race classes."
      },
      {
        "type": "ul",
        "items": [
          "Each actor handles one message at a time — serializes its own state.",
          "Location transparency — actors can be remote.",
          "Supervision trees restart failed actors — resilience pattern."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Compare to CSP (Go channels) and thread+lock model. Message passing scales mentally for distributed systems."
      },
      {
        "type": "h",
        "text": "Go channels comparison"
      },
      {
        "type": "p",
        "text": "Go: \"Do not communicate by sharing memory; share memory by communicating.\" Channels serialize access like actors with bounded buffers."
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Akka persistence stores actor state as event stream — links to event sourcing concepts."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Actors & Message Passing show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "OSTEP — Operating Systems",
        "url": "https://pages.cs.wisc.edu/~remzi/OSTEP/"
      }
    ]
  },
  "authentication": {
    "title": "Authentication",
    "subtitle": "Proving who the caller is — before authorization decides what they can do.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "Authentication verifies identity (password, OAuth, mTLS, API key). Always hash passwords (bcrypt/Argon2), rate-limit login, and use MFA for sensitive apps."
      },
      {
        "type": "steps",
        "items": [
          {
            "title": "Register",
            "text": "Hash password, never store plaintext."
          },
          {
            "title": "Login",
            "text": "Verify hash, issue session or token."
          },
          {
            "title": "Subsequent requests",
            "text": "Validate cookie/JWT/API key on each call."
          },
          {
            "title": "Logout / revoke",
            "text": "Invalidate session store or token blocklist."
          }
        ]
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Session fixation, credential stuffing, and timing attacks — use established libraries, not homemade crypto."
      },
      {
        "type": "h",
        "text": "Threat model basics"
      },
      {
        "type": "ul",
        "items": [
          "Brute force → rate limit + lockout + MFA.",
          "Session hijack → HttpOnly Secure cookies, short TTL.",
          "CSRF → SameSite cookies + CSRF tokens on state-changing forms."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Compare session cookie vs JWT in browser — XSS steals both; HttpOnly helps cookies; JWT in memory avoids automatic send but XSS still exfiltrates."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Authentication show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "OAuth 2.0 RFC 6749",
        "url": "https://datatracker.ietf.org/doc/html/rfc6749"
      },
      {
        "label": "JWT.io Introduction",
        "url": "https://jwt.io/introduction"
      }
    ]
  },
  "jwt": {
    "title": "JWT",
    "subtitle": "JSON Web Token — signed (or encrypted) claims passed statelessly between services.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "JWT has header.payload.signature (Base64url). Server verifies signature with secret/public key — no session store required for validation."
      },
      {
        "type": "ul",
        "items": [
          "Short expiry (minutes) + refresh tokens for long sessions.",
          "Store sensitive claims minimally — payload is readable (Base64), not secret.",
          "Rotate signing keys (JWKS endpoint) for zero-downtime rotation.",
          "Revocation hard — use blocklist or short TTL."
        ]
      },
      {
        "type": "code",
        "language": "text",
        "title": "JWT structure",
        "code": "eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiIxMjM0In0.signature"
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "JWT vs opaque session ID: JWT scales across services but harder to revoke; session ID needs central store but instant revoke."
      },
      {
        "type": "h",
        "text": "Algorithms"
      },
      {
        "type": "table",
        "headers": [
          "Alg",
          "Notes"
        ],
        "rows": [
          [
            "HS256",
            "Shared secret — all verifiers need secret"
          ],
          [
            "RS256",
            "Private sign, public verify — better for microservices"
          ],
          [
            "none",
            "Never accept — algorithm confusion attacks"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Validate alg header explicitly — do not allow attacker to switch RS256 to HS256 with public key as secret."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "JWT.io Introduction",
        "url": "https://jwt.io/introduction"
      }
    ]
  },
  "oauth": {
    "title": "OAuth 2.0",
    "subtitle": "Delegated authorization — let users grant limited access without sharing passwords.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "OAuth 2.0 flows exchange authorization codes or tokens between client, authorization server, and resource server. OpenID Connect adds identity layer on top."
      },
      {
        "type": "table",
        "headers": [
          "Flow",
          "Use"
        ],
        "rows": [
          [
            "Authorization Code + PKCE",
            "SPAs and mobile — modern default"
          ],
          [
            "Client Credentials",
            "Service-to-service, no user"
          ],
          [
            "Device Code",
            "TVs and CLI devices"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "warn",
        "title": "Security",
        "text": "Never expose client secrets in mobile/SPA. Use PKCE. Validate redirect_uri exactly. Store refresh tokens securely."
      },
      {
        "type": "h",
        "text": "Tokens explained"
      },
      {
        "type": "ul",
        "items": [
          "Authorization code: one-time, exchanged server-side for tokens.",
          "Access token: short-lived API access.",
          "Refresh token: long-lived, stored securely, rotates on use.",
          "ID token (OIDC): JWT with user identity claims."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Draw authorization code flow on whiteboard — include PKCE code_verifier for public clients."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does OAuth 2.0 show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "OAuth 2.0 RFC 6749",
        "url": "https://datatracker.ietf.org/doc/html/rfc6749"
      }
    ]
  },
  "rbac": {
    "title": "RBAC",
    "subtitle": "Role-Based Access Control — permissions attached to roles, users get roles.",
    "readingMinutes": 8,
    "blocks": [
      {
        "type": "p",
        "text": "RBAC simplifies policy: admin, editor, viewer roles instead of per-user permission lists. Check role or permission on every protected endpoint."
      },
      {
        "type": "ul",
        "items": [
          "Enforce on server — UI hiding buttons is not security.",
          "Role hierarchy: admin inherits editor permissions.",
          "Attribute-based (ABAC) extends RBAC with resource attributes (owner_id)."
        ]
      },
      {
        "type": "code",
        "language": "java",
        "title": "Spring-style check",
        "code": "@PreAuthorize(\"hasRole('ADMIN')\")\npublic void deleteUser(Long id) { ... }"
      },
      {
        "type": "h",
        "text": "Policy models beyond RBAC"
      },
      {
        "type": "p",
        "text": "ABAC adds attributes (department, resource owner). ReBAC (relationship-based) like Google Zanzibar models \"user X can edit doc Y if member of folder Z.\""
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Centralize authorization — duplicating checks in every service leads to inconsistent enforcement."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does RBAC show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "The Twelve-Factor App",
        "url": "https://12factor.net/"
      }
    ]
  },
  "redis": {
    "title": "Redis",
    "subtitle": "In-memory data store used as cache, session store, rate limiter, and pub/sub bus.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "Redis offers sub-millisecond reads for hot keys: strings, hashes, lists, sets, sorted sets, streams. Persistence optional (RDB/AOF)."
      },
      {
        "type": "h",
        "text": "Backend implementation patterns"
      },
      {
        "type": "ul",
        "items": [
          "Cache-aside: read miss → load DB → SET with TTL.",
          "Distributed lock: SET key NX EX — always set expiry; use fencing tokens for correctness.",
          "Rate limit: INCR + EXPIRE or sliding window with sorted sets.",
          "Session store: centralized logout across app instances."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Cache stampede: lock or probabilistic early expiration. Hot key: local L1 + Redis sharding."
      },
      {
        "type": "code",
        "language": "redis",
        "title": "Cache-aside read",
        "code": "GET user:42\n# on miss:\nSET user:42 \"{json}\" EX 300"
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Redis show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Strong candidates connect Redis to a real project story — prepare one example before the interview."
      }
    ],
    "references": [
      {
        "label": "Redis Documentation",
        "url": "https://redis.io/docs/"
      }
    ]
  },
  "caching": {
    "title": "Caching",
    "subtitle": "Storing computed or fetched data closer to the consumer to cut latency and load.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "Caches exist at every layer: CPU L1-L3, page cache, CDN, application Redis, ORM second-level cache. Each has different TTL and invalidation rules."
      },
      {
        "type": "table",
        "headers": [
          "Pattern",
          "Behavior"
        ],
        "rows": [
          [
            "Cache-aside",
            "App manages read/write; lazy populate"
          ],
          [
            "Write-through",
            "Write cache + DB together"
          ],
          [
            "Write-behind",
            "Async DB write — faster, riskier"
          ],
          [
            "Read-through",
            "Cache library loads on miss"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "warn",
        "title": "Invalidation",
        "text": "\"There are only two hard things…\" — define TTL + event-driven invalidation on writes."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "CS angle: implement cache-aside in code; HLD angle: CDN + Redis cluster — both valid, different depth."
      },
      {
        "type": "h",
        "text": "Cache problems to name in interviews"
      },
      {
        "type": "ul",
        "items": [
          "Cache stampede / thundering herd on hot key expiry.",
          "Stale reads after write — TTL vs active invalidation.",
          "Cold start empty cache after deploy — gradual warmup or prefill."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Quantify hit ratio target: 95%+ for read-heavy catalog; measure evictions and memory cap."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "Redis Documentation",
        "url": "https://redis.io/docs/"
      }
    ]
  },
  "kafka": {
    "title": "Kafka",
    "subtitle": "Distributed commit log for high-throughput event streaming between services.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "Kafka topics partition for parallelism; consumers track offset per partition. Replication factor gives durability; ISR elects leader on failure."
      },
      {
        "type": "ul",
        "items": [
          "Producers key by user_id → ordered events per user within partition.",
          "Consumer groups scale consumption — one consumer per partition max.",
          "Retention by time/size — replayable log, not classic queue delete-on-read.",
          "Exactly-once needs idempotent producer + transactional writes carefully."
        ]
      },
      {
        "type": "diagram",
        "kind": "queue",
        "caption": "Producers append to partitions; consumer groups read at independent offsets."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Kafka vs RabbitMQ: log vs queue; replay; ordering per partition; ops complexity trade-off."
      },
      {
        "type": "h",
        "text": "Operational concerns"
      },
      {
        "type": "ul",
        "items": [
          "Under-replicated partitions during broker failure.",
          "Consumer lag monitoring — alert before hours behind.",
          "Rebalance storm on consumer join/leave — static membership helps."
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Idempotent producer + acks=all for durability; min.insync.replicas=2 on replicas."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "Apache Kafka Documentation",
        "url": "https://kafka.apache.org/documentation/"
      }
    ]
  },
  "rabbitmq": {
    "title": "RabbitMQ",
    "subtitle": "Message broker with exchanges, queues, and flexible routing — great for task workers.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "RabbitMQ routes messages via exchanges (direct, topic, fanout) to queues. Consumers ack messages; nack/requeue or dead-letter on failure."
      },
      {
        "type": "ul",
        "items": [
          "Work queues — round-robin tasks to workers.",
          "Pub/sub — fanout exchange to many queues.",
          "Dead-letter exchange (DLX) for poison messages.",
          "Prefetch count limits unacked backlog per consumer."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "When Rabbit beats Kafka: per-message ack, classic job queue, lower operational footprint for moderate scale."
      },
      {
        "type": "h",
        "text": "Reliability settings"
      },
      {
        "type": "ul",
        "items": [
          "Publisher confirms — broker acked message.",
          "Consumer manual ack after successful processing.",
          "Quorum queues for HA (modern Rabbit)."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Message loss scenarios: auto-ack before processing finishes — always manual ack after work completes."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does RabbitMQ show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "RabbitMQ Documentation",
        "url": "https://www.rabbitmq.com/documentation.html"
      }
    ]
  },
  "docker": {
    "title": "Docker",
    "subtitle": "Package app + dependencies into images; run isolated containers on shared kernel.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "Containers share host kernel but get isolated namespaces (pid, net, mount) and cgroups (CPU/memory limits). Images layer filesystem diffs immutably."
      },
      {
        "type": "ul",
        "items": [
          "Dockerfile: multi-stage builds shrink prod images.",
          "Never run as root in prod; read-only root FS where possible.",
          "Healthcheck + graceful SIGTERM handling for orchestrators.",
          "Volumes for persistent data — container FS is ephemeral."
        ]
      },
      {
        "type": "code",
        "language": "dockerfile",
        "title": "Multi-stage snippet",
        "code": "FROM golang:1.22 AS build\nWORKDIR /src\nCOPY . .\nRUN go build -o /app\n\nFROM gcr.io/distroless/base\nCOPY --from=build /app /app\nENTRYPOINT [\"/app\"]"
      },
      {
        "type": "h",
        "text": "Networking modes"
      },
      {
        "type": "ul",
        "items": [
          "bridge — default isolated network.",
          "host — container shares host network stack.",
          "overlay — multi-host Swarm/K8s CNI."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Image immutability: tag :latest bad for prod — pin digest or semver tag for reproducible deploys."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Docker show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Docker Documentation",
        "url": "https://docs.docker.com/"
      }
    ]
  },
  "kubernetes": {
    "title": "Kubernetes",
    "subtitle": "Orchestrates containers — scheduling, scaling, networking, and self-healing.",
    "readingMinutes": 11,
    "blocks": [
      {
        "type": "p",
        "text": "K8s runs desired state: Deployments manage ReplicaSets; Services stable DNS/load balance; Ingress HTTP routing; ConfigMaps/Secrets inject config."
      },
      {
        "type": "table",
        "headers": [
          "Object",
          "Role"
        ],
        "rows": [
          [
            "Pod",
            "One or more containers"
          ],
          [
            "Deployment",
            "Rolling updates, desired replicas"
          ],
          [
            "Service",
            "ClusterIP/LoadBalancer endpoints"
          ],
          [
            "HPA",
            "Autoscale on CPU/custom metrics"
          ],
          [
            "Probe",
            "Liveness vs readiness"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Readiness failing → removed from Service endpoints; liveness failing → pod restart. Know difference."
      },
      {
        "type": "h",
        "text": "Rolling update parameters"
      },
      {
        "type": "p",
        "text": "maxUnavailable and maxSurge control rollout speed. readinessProbe must reflect app actually serving — not just JVM started."
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "OOMKilled pod — check limits vs requests; memory request affects scheduling, limit triggers kill."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Explain Pod → Deployment → ReplicaSet relationship — who owns what on kubectl apply."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "Kubernetes Documentation",
        "url": "https://kubernetes.io/docs/"
      }
    ]
  },
  "rate-limiter": {
    "title": "Rate Limiter",
    "subtitle": "Protect services by capping requests per user, IP, or API key over time.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "Rate limiting prevents abuse and overload. Algorithms: token bucket (smooth burst), leaky bucket (steady outflow), fixed/sliding window counters."
      },
      {
        "type": "code",
        "language": "redis",
        "title": "Token bucket sketch (Redis)",
        "code": "-- INCR tokens, compare to limit, EXPIRE key\nlocal c = redis.call(\"INCR\", KEYS[1])\nif c == 1 then redis.call(\"EXPIRE\", KEYS[1], ARGV[2]) end\nreturn c <= tonumber(ARGV[1])"
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Return 429 with Retry-After header. Distributed limiters need shared store (Redis) or approximate edge counters."
      },
      {
        "type": "h",
        "text": "Where to enforce"
      },
      {
        "type": "ul",
        "items": [
          "Edge API gateway — protect whole cluster.",
          "Per-service — finer quotas per endpoint.",
          "Client SDK retry with exponential backoff on 429."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Compare token bucket (allows burst) vs leaky bucket (smooth output) for product requirements."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Rate Limiter show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Redis Documentation",
        "url": "https://redis.io/docs/"
      }
    ]
  },
  "circuit-breaker": {
    "title": "Circuit Breaker",
    "subtitle": "Stop calling a failing dependency — fail fast and recover gradually.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "States: CLOSED (normal), OPEN (reject calls), HALF-OPEN (trial requests). Prevents cascade failures when downstream is unhealthy."
      },
      {
        "type": "steps",
        "items": [
          {
            "title": "CLOSED",
            "text": "Track failure rate / latency."
          },
          {
            "title": "OPEN",
            "text": "Threshold exceeded — immediate fallback/error."
          },
          {
            "title": "HALF-OPEN",
            "text": "After cooldown, allow probe requests."
          },
          {
            "title": "Recover",
            "text": "Successes close circuit; failures re-open."
          }
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Libraries: Resilience4j, Polly. Pair with timeouts and bulkheads — breaker alone is not enough."
      },
      {
        "type": "h",
        "text": "Fallback strategies"
      },
      {
        "type": "ul",
        "items": [
          "Return cached stale data with warning header.",
          "Degrade feature — skip recommendations, core checkout works.",
          "Queue request for async retry."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Bulkhead thread pools isolate dependency failures — payment pool separate from catalog pool."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Circuit Breaker show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "The Twelve-Factor App",
        "url": "https://12factor.net/"
      }
    ]
  },
  "distributed-lock": {
    "title": "Distributed Lock",
    "subtitle": "Single winner across multiple app instances for cron, migration, or leader tasks.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "Use when you must coordinate across nodes: only one worker processes partition, or nightly job runs once cluster-wide."
      },
      {
        "type": "ul",
        "items": [
          "Redis SET NX EX with short TTL + heartbeat renew.",
          "DB advisory locks or lease table with expiry.",
          "Fencing tokens: monotonic token passed to storage so stale lock holders cannot write."
        ]
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Clock skew and GC pauses can exceed TTL — always fence downstream writes."
      },
      {
        "type": "h",
        "text": "Alternatives to locks"
      },
      {
        "type": "p",
        "text": "Leader election via K8s Lease API. Database UNIQUE constraint for idempotent job insert. Kafka partition assignment ensures one consumer per partition."
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Redlock controversy (Martin Kleppmann) — understand fencing tokens for correctness under delay."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Distributed Lock show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Redis Documentation",
        "url": "https://redis.io/docs/"
      }
    ]
  },
  "microservices": {
    "title": "Microservices",
    "subtitle": "Small independently deployable services with bounded contexts — trade autonomy for ops complexity.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "Microservices enable team ownership, polyglot stacks, and independent scaling. Cost: network latency, distributed debugging, data consistency, deployment pipelines."
      },
      {
        "type": "ul",
        "items": [
          "Start monolith until boundaries clear — premature split hurts.",
          "API contracts + versioning; consumer-driven contracts help.",
          "Observability and tracing become mandatory.",
          "Avoid distributed monolith — tight synchronous coupling."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "When to split: different scaling profiles, release cadence, or regulatory isolation — not \"because Netflix.\""
      },
      {
        "type": "h",
        "text": "Communication styles"
      },
      {
        "type": "table",
        "headers": [
          "Style",
          "Coupling"
        ],
        "rows": [
          [
            "Sync REST/gRPC",
            "Tight — cascading latency"
          ],
          [
            "Async events",
            "Loose — eventual consistency"
          ],
          [
            "Shared DB anti-pattern",
            "Very tight — avoid"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Domain-Driven Design bounded contexts guide service boundaries — align with business language."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Microservices show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "The Twelve-Factor App",
        "url": "https://12factor.net/"
      }
    ]
  },
  "saga": {
    "title": "Saga",
    "subtitle": "Multi-step business transaction across services using compensating actions instead of 2PC.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "Each step commits locally; on failure, run compensating transactions (cancel reservation, refund payment). Choreography (events) vs orchestration (central coordinator)."
      },
      {
        "type": "steps",
        "items": [
          {
            "title": "Reserve inventory",
            "text": "Local tx in inventory service."
          },
          {
            "title": "Charge payment",
            "text": "Local tx in payment service."
          },
          {
            "title": "Failure",
            "text": "Compensate: release inventory, refund if charged."
          }
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Sagas are eventually consistent — design idempotent steps and visible pending states for users."
      },
      {
        "type": "h",
        "text": "Choreography vs orchestration"
      },
      {
        "type": "table",
        "headers": [
          "",
          "Choreography",
          "Orchestration"
        ],
        "rows": [
          [
            "Coordination",
            "Events between services",
            "Central saga manager"
          ],
          [
            "Pros",
            "Loose coupling",
            "Clear visibility"
          ],
          [
            "Cons",
            "Hard to trace flow",
            "Orchestrator SPOF risk"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Compensating transactions are business logic — \"cancel\" may mean refund fees differently per product."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Saga show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Apache Kafka Documentation",
        "url": "https://kafka.apache.org/documentation/"
      }
    ]
  },
  "cqrs": {
    "title": "CQRS",
    "subtitle": "Command Query Responsibility Segregation — separate write and read models.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "Commands mutate domain state on write model; queries hit optimized read projections. Useful when read patterns differ wildly from writes."
      },
      {
        "type": "ul",
        "items": [
          "Enables scaling reads independently (replicas, Elasticsearch).",
          "Often paired with Event Sourcing — not required.",
          "Cost: eventual consistency on read side; more moving parts."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Do not CQRS a CRUD admin panel — use when read models need denormalization for scale or complex reporting."
      },
      {
        "type": "h",
        "text": "Read model update paths"
      },
      {
        "type": "ol",
        "items": [
          "Dual write (risky — inconsistency window).",
          "Change data capture (CDC) from write DB to read store.",
          "Domain events projected asynchronously."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Users see stale read for seconds — product must tolerate or show version/conflict UI."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does CQRS show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "The Twelve-Factor App",
        "url": "https://12factor.net/"
      }
    ]
  },
  "event-sourcing": {
    "title": "Event Sourcing",
    "subtitle": "Store state changes as append-only events — current state is replay or snapshot.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "Instead of UPDATE row, append OrderCreated, ItemAdded, OrderPaid events. Rebuild aggregate by replay; snapshots speed recovery."
      },
      {
        "type": "ul",
        "items": [
          "Audit trail for free — who did what when.",
          "Temporal queries: state at any point in time.",
          "Projections build read models asynchronously."
        ]
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Schema evolution of events requires upcasting/versioning strategy — plan upfront."
      },
      {
        "type": "h",
        "text": "When it helps"
      },
      {
        "type": "ul",
        "items": [
          "Audit-heavy domains: finance, healthcare.",
          "Temporal debugging — replay production bug.",
          "Multiple read projections from same event log."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Event schema evolution: upcast old events on read or maintain versioned deserializers."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Event Sourcing show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Apache Kafka Documentation",
        "url": "https://kafka.apache.org/documentation/"
      }
    ]
  },
  "observability": {
    "title": "Observability",
    "subtitle": "Understanding system behavior from the outside via metrics, logs, and traces.",
    "readingMinutes": 10,
    "blocks": [
      {
        "type": "p",
        "text": "Monitoring tells you known failures; observability helps debug unknown unknowns. Three pillars: metrics (aggregates), logs (events), traces (request paths)."
      },
      {
        "type": "ul",
        "items": [
          "Golden signals: latency, traffic, errors, saturation.",
          "SLI/SLO/ error budgets drive reliability culture.",
          "Correlate trace_id across services for distributed debugging."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Describe debugging p99 spike: metrics dashboard → trace slow span → logs at timestamp → root cause."
      },
      {
        "type": "h",
        "text": "USE and RED methods"
      },
      {
        "type": "table",
        "headers": [
          "Method",
          "For",
          "Metrics"
        ],
        "rows": [
          [
            "USE",
            "Resources",
            "Utilization, Saturation, Errors"
          ],
          [
            "RED",
            "Services",
            "Rate, Errors, Duration"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Alert on symptoms (user-facing SLO breach) not every cause — on-call fatigue kills response quality."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Observability show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "OpenTelemetry Docs",
        "url": "https://opentelemetry.io/docs/"
      }
    ]
  },
  "prometheus": {
    "title": "Prometheus",
    "subtitle": "Pull-based metrics TSDB with PromQL — de facto Kubernetes monitoring.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "Prometheus scrapes /metrics endpoints on interval, stores time series, alerts via Alertmanager. Exporters expose DB, node, and app metrics."
      },
      {
        "type": "code",
        "language": "promql",
        "title": "Example queries",
        "code": "rate(http_requests_total[5m])\nhistogram_quantile(0.99, rate(http_request_duration_seconds_bucket[5m]))"
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Label cardinality kills Prometheus — avoid unbounded user_id labels."
      },
      {
        "type": "h",
        "text": "Instrumentation in code"
      },
      {
        "type": "code",
        "language": "java",
        "title": "Counter and histogram",
        "code": "Counter requests = Counter.build(\"http_requests_total\", \"help\").register();\nHistogram latency = Histogram.build(\"http_request_duration_seconds\", \"help\").register();\nrequests.inc();\nlatency.observe(durationSec);"
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Counter vs Gauge vs Histogram — pick correct type; Gauge for queue depth, Counter for total requests only increases."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Prometheus show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Prometheus Documentation",
        "url": "https://prometheus.io/docs/"
      }
    ]
  },
  "grafana": {
    "title": "Grafana",
    "subtitle": "Visualization and dashboards atop Prometheus, Loki, Tempo, and other datasources.",
    "readingMinutes": 8,
    "blocks": [
      {
        "type": "p",
        "text": "Grafana builds dashboards, variables, and alerts on metrics/logs/traces. Use consistent dashboard templates per service."
      },
      {
        "type": "ul",
        "items": [
          "RED method dashboards: Rate, Errors, Duration.",
          "Link traces to logs with trace_id filter.",
          "On-call runbooks linked from alert annotations."
        ]
      },
      {
        "type": "h",
        "text": "On-call dashboard essentials"
      },
      {
        "type": "ul",
        "items": [
          "Golden signals per service row.",
          "Deploy markers correlated with error spikes.",
          "Links from panel to runbook and recent traces."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Dashboard as code (Jsonnet/Terraform) — reviewable changes like application code."
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Use variables for environment and service — one dashboard template for all microservices."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does Grafana show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "Grafana Documentation",
        "url": "https://grafana.com/docs/"
      }
    ]
  },
  "opentelemetry": {
    "title": "OpenTelemetry",
    "subtitle": "Vendor-neutral instrumentation for traces, metrics, and logs.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "OTel SDKs auto/manual instrument HTTP, DB, messaging. OTLP exports to Jaeger, Tempo, Datadog, etc. — avoid vendor lock-in at instrumentation layer."
      },
      {
        "type": "steps",
        "items": [
          {
            "title": "Instrument",
            "text": "Add OTel SDK + propagators (W3C traceparent)."
          },
          {
            "title": "Collect",
            "text": "Sidecar agent or centralized collector."
          },
          {
            "title": "Export",
            "text": "Backend of choice for storage/query."
          }
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Sampling head-based vs tail-based — balance cost vs always catching errors."
      },
      {
        "type": "h",
        "text": "Context propagation"
      },
      {
        "type": "p",
        "text": "W3C traceparent header carries trace-id and span-id across HTTP and messaging. Broken propagation creates orphan spans — useless distributed traces."
      },
      {
        "type": "callout",
        "tone": "warn",
        "text": "Auto-instrumentation misses custom thread pools — manual context attach when handing off work."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      },
      {
        "type": "ul",
        "items": [
          "How does OpenTelemetry show up in production debugging?",
          "What trade-offs would you discuss with a senior interviewer?",
          "Name one failure mode and how you would detect it in metrics or logs."
        ]
      }
    ],
    "references": [
      {
        "label": "OpenTelemetry Docs",
        "url": "https://opentelemetry.io/docs/"
      }
    ]
  },
  "structured-logging": {
    "title": "Structured Logging",
    "subtitle": "Machine-parseable logs (JSON) with consistent fields — searchable in production.",
    "readingMinutes": 9,
    "blocks": [
      {
        "type": "p",
        "text": "Structured logs emit key-value JSON: timestamp, level, service, trace_id, user_id, message. Enables log aggregation (ELK, Loki) and correlation with traces."
      },
      {
        "type": "code",
        "language": "json",
        "title": "Example log line",
        "code": "{\"ts\":\"2026-07-26T10:00:00Z\",\"level\":\"ERROR\",\"service\":\"payments\",\"trace_id\":\"abc123\",\"order_id\":9912,\"msg\":\"charge failed\",\"err\":\"timeout\"}"
      },
      {
        "type": "ul",
        "items": [
          "Never log secrets, full PAN, or passwords.",
          "Use log levels consistently — ERROR means action needed.",
          "Include request/correlation IDs from ingress."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Contrast printf debugging vs searchable fields: \"find all errors for order_id=X in last hour.\""
      },
      {
        "type": "h",
        "text": "Log aggregation pipeline"
      },
      {
        "type": "ol",
        "items": [
          "App emits JSON to stdout.",
          "Agent (Fluent Bit, Promtail) ships to Loki/ELK.",
          "Index on trace_id, level, service — not free-text only.",
          "Retention and PII scrubbing policies applied."
        ]
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Debug prod with trace_id from user report → find all logs and spans for that request in one click."
      },
      {
        "type": "h",
        "text": "Common interview follow-ups"
      }
    ],
    "references": [
      {
        "label": "OpenTelemetry Docs",
        "url": "https://opentelemetry.io/docs/"
      }
    ]
  }
};
